import { observer } from 'mobx-react';
import React from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useTabs } from '../../components/hooks/use-tabs';
import { useWrapRequest } from '../../components/hooks/use-wrap-request';
import { PageHeader } from '@components/page-header';
import { PageTabs } from '@components/page-tabs';
import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Api } from '../../core/api';
import { injectTSDI } from '../../core/tsdi';
import { CommunityDetailsSupportDto } from '@api/ChallengeClient';

const pageTabs = [
    { label: 'Details', id: 'details' },
    { label: 'Feed', id: 'feed' },
    { label: 'Members', id: 'members' },
    { label: 'Teams', id: 'teams' },
    { label: 'Goals', id: 'goals' },
    { label: 'Challenges', id: 'challenges' },
    { label: 'Settings', id: 'settings' },
    { label: 'Whitelabel', id: 'whitelabel' },
    { label: 'Leaderboards', id: 'leaderboard-preview' },
    { label: 'Point Definitions', id: 'point-definitions' },
    { label: 'Facility Restrictions', id: 'facility-restrictions' },
    { label: 'Locations', id: 'locations' },
    { label: 'Budget', id: 'budget' }
];

export const CommunityContext = React.createContext<CommunityDetailsSupportDto>(
    {} as CommunityDetailsSupportDto
);

export default observer(() => {
    const { communityId } = useParams();

    const navigate = useNavigate();
    const { pathname } = useLocation();

    const { tab, getPageLink, setPageTab } = useTabs(pageTabs);
    const { challengeClient } = injectTSDI(Api);

    const communityRequest = useWrapRequest<CommunityDetailsSupportDto, any>(
        async () => {
            const response =
                await challengeClient.support.communitySupportControllerV2Get(
                    parseInt(communityId)
                );
            return response.data;
        },
        {
            deps: []
        }
    );
    const changePage = (page: number) => {
        const pageLink = getPageLink(page);
        navigate(pageLink);
    };

    React.useEffect(() => {
        setPageTab(pathname, 3);
    }, [pathname]);

    return (
        <>
            {communityRequest.match({
                loading: () => (
                    <div className="flex min-h-[400px] items-center justify-center">
                        <Spinner />
                    </div>
                ),
                error: () => (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {communityRequest.error?.message}
                        </AlertDescription>
                    </Alert>
                ),
                fetched: (community) => (
                    <CommunityContext.Provider value={community}>
                        <PageHeader
                            title={community.name}
                            badges={
                                <>
                                    <Badge
                                        variant={
                                            community.approved
                                                ? 'success'
                                                : 'warning'
                                        }
                                    >
                                        {community.approved
                                            ? 'Approved'
                                            : 'Not approved'}
                                    </Badge>
                                    {community.visibility && (
                                        <Badge variant="outline">
                                            {community.visibility}
                                        </Badge>
                                    )}
                                </>
                            }
                        />
                        <PageTabs
                            tabs={pageTabs}
                            value={tab}
                            onChange={changePage}
                        />
                        <div className="mt-6">
                            <Outlet />
                        </div>
                    </CommunityContext.Provider>
                )
            })}
        </>
    );
});
